import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import '../styles/ChangePassword.css'

const BASE_URL = 'https://api.admin.yourhood.ge'

const STEPS = {
  PHONE: 'phone',
  OTP: 'otp',
  PASSWORD: 'password',
  DONE: 'done'
}

function ChangePassword() {
  const [step, setStep] = useState(STEPS.PHONE)
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [key, setKey] = useState('')
  const [sessionID, setSessionID] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const clearError = () => setError('')

  // Step 1: request recovery key then send OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault()
    clearError()
    if (!phone.trim()) {
      setError('გთხოვთ შეიყვანოთ ტელეფონის ნომერი')
      return
    }
    setIsLoading(true)
    try {
      const res = await fetch(
        `${BASE_URL}/api/Auth/request-recover-password?phoneNumber=${encodeURIComponent(phone)}`,
        { headers: { 'X-Language': 'ka' } }
      )
      const data = await res.json()
      if (!res.ok || !data.key) {
        setError('ნომერი ვერ მოიძებნა. სცადეთ თავიდან.')
        return
      }
      const recoveryKey = data.key
      setKey(recoveryKey)

      const otpRes = await fetch(
        `${BASE_URL}/api/Auth/send-otp?key=${encodeURIComponent(recoveryKey)}`,
        { method: 'POST', headers: { 'X-Language': 'ka' } }
      )
      const otpSent = await otpRes.json()
      if (!otpRes.ok || !otpSent) {
        setError('OTP გაგზავნა ვერ მოხერხდა. სცადეთ თავიდან.')
        return
      }
      setStep(STEPS.OTP)
    } catch {
      setError('კავშირის შეცდომა. სცადეთ თავიდან.')
    } finally {
      setIsLoading(false)
    }
  }

  // Step 2: verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault()
    clearError()
    if (!otp.trim()) {
      setError('გთხოვთ შეიყვანოთ კოდი')
      return
    }
    setIsLoading(true)
    try {
      const res = await fetch(
        `${BASE_URL}/api/Auth/verify-otp?key=${encodeURIComponent(key)}&otp=${encodeURIComponent(otp)}`,
        { method: 'POST', headers: { 'X-Language': 'ka' } }
      )
      const data = await res.json()
      if (!res.ok || !data.sessionID) {
        setError('კოდი არასწორია. სცადეთ თავიდან.')
        return
      }
      setSessionID(data.sessionID)
      setStep(STEPS.PASSWORD)
    } catch {
      setError('კავშირის შეცდომა. სცადეთ თავიდან.')
    } finally {
      setIsLoading(false)
    }
  }

  // Step 3: set new password
  const handleSetPassword = async (e) => {
    e.preventDefault()
    clearError()
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('გთხოვთ შეავსოთ ყველა ველი')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('პაროლები არ ემთხვევა')
      return
    }
    setIsLoading(true)
    try {
      const res = await fetch(`${BASE_URL}/api/Auth/recover-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Language': 'ka',
          'Authorization': `Bearer ${sessionID}`
        },
        body: JSON.stringify({ newPassword, confirmPassword })
      })
      if (res.ok) {
        setStep(STEPS.DONE)
        setTimeout(() => navigate('/'), 2500)
      } else {
        setError('პაროლის შეცვლა ვერ მოხერხდა. სცადეთ თავიდან.')
      }
    } catch {
      setError('კავშირის შეცდომა. სცადეთ თავიდან.')
    } finally {
      setIsLoading(false)
    }
  }

  const stepIndex = [STEPS.PHONE, STEPS.OTP, STEPS.PASSWORD, STEPS.DONE].indexOf(step)

  return (
    <div className="cp-container">
      <div className="cp-card">

        <div className="cp-header">
          <span className="cp-brand">YourHood</span>
          <h1 className="cp-title">პაროლის აღდგენა</h1>
          <p className="cp-subtitle">
            {step === STEPS.PHONE && 'შეიყვანეთ თქვენი ნომერი'}
            {step === STEPS.OTP && 'შეიყვანეთ SMS კოდი'}
            {step === STEPS.PASSWORD && 'შექმენით ახალი პაროლი'}
            {step === STEPS.DONE && 'პაროლი წარმატებით შეიცვალა'}
          </p>
        </div>

        {/* Step indicator */}
        <div className="cp-steps">
          {[STEPS.PHONE, STEPS.OTP, STEPS.PASSWORD].map((s, i) => (
            <div key={s} className="cp-step-item">
              <div className={`cp-step-dot ${step === s ? 'active' : stepIndex > i ? 'done' : ''}`}>
                {stepIndex > i ? '✓' : i + 1}
              </div>
              {i < 2 && <div className={`cp-step-line ${stepIndex > i ? 'done' : ''}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1 — Phone */}
        {step === STEPS.PHONE && (
          <form onSubmit={handleRequestOtp} className="cp-form">
            <div className="cp-form-group">
              <label className="cp-label">ტელეფონის ნომერი</label>
              <input
                type="tel"
                className="cp-input"
                placeholder="5XXXXXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={isLoading}
                autoComplete="tel"
              />
            </div>
            {error && <div className="cp-error">{error}</div>}
            <button type="submit" className="cp-button" disabled={isLoading}>
              {isLoading ? 'გაგზავნა...' : 'კოდის გაგზავნა'}
            </button>
          </form>
        )}

        {/* STEP 2 — OTP */}
        {step === STEPS.OTP && (
          <form onSubmit={handleVerifyOtp} className="cp-form">
            <div className="cp-form-group">
              <label className="cp-label">SMS კოდი</label>
              <input
                type="text"
                className="cp-input cp-input-otp"
                placeholder="· · · ·"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={isLoading}
                autoComplete="one-time-code"
              />
            </div>
            {error && <div className="cp-error">{error}</div>}
            <button type="submit" className="cp-button" disabled={isLoading}>
              {isLoading ? 'შემოწმება...' : 'კოდის დადასტურება'}
            </button>
            <button
              type="button"
              className="cp-button-ghost"
              onClick={() => { setStep(STEPS.PHONE); clearError() }}
            >
              ← უკან
            </button>
          </form>
        )}

        {/* STEP 3 — New Password */}
        {step === STEPS.PASSWORD && (
          <form onSubmit={handleSetPassword} className="cp-form">
            <div className="cp-form-group">
              <label className="cp-label">ახალი პაროლი</label>
              <input
                type="password"
                className="cp-input"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>
            <div className="cp-form-group">
              <label className="cp-label">გაიმეორეთ პაროლი</label>
              <input
                type="password"
                className="cp-input"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>
            {error && <div className="cp-error">{error}</div>}
            <button type="submit" className="cp-button" disabled={isLoading}>
              {isLoading ? 'შენახვა...' : 'პაროლის შეცვლა'}
            </button>
          </form>
        )}

        {/* DONE */}
        {step === STEPS.DONE && (
          <div className="cp-done">
            <div className="cp-done-icon">✓</div>
            <p className="cp-done-text">პაროლი წარმატებით შეიცვალა. გადამისამართება...</p>
          </div>
        )}

        <div className="cp-links">
          <Link to="/" className="cp-link">შესვლა</Link>
          <span className="cp-link-sep">·</span>
          <Link to="/register" className="cp-link">რეგისტრაცია</Link>
        </div>

        <div className="cp-footer">
          <span className="cp-footer-text">© 2026 YourHood · დაცული კავშირი</span>
        </div>

      </div>
    </div>
  )
}

export default ChangePassword